import React, { useState } from 'react';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import { useDeleteFormMutation, useEnableFormMutation } from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button, { Props as ButtonProps } from '~/components/atom/Button';
import useAddToast from '~/hooks/useAddToast';
import TextButton from '~/components/atom/TextButton';
import { Body, Heading3, Heading4 } from '~/components/atom/Typography';
import executeBatchMutation from '~/components/page/Automation/v2/components/FlowList/components/FlowListToolbar/utils/executeBatchMutation';
import getFormText from '../../utils/getFormsText';
import type { ActionType } from '../../constants';
import useTrackEvent from '~/hooks/useTrackEvent';
import type { SelectedForm } from '../../../..';

const text = {
  cancelLabel: 'Annuleren',
  actions: {
    delete: 'Verwijder',
    activate: 'Activeer',
    deactivate: 'Deactiveer',
  },
  loading: {
    delete: 'Verwijderen...',
    activate: 'Activeren...',
    deactivate: 'Deactiveren...',
  },
  selectedFlows: 'geselecteerde flows',
  affectedForms: (count: number) =>
    `De volgende ${count === 1 ? 'formulier' : 'formulieren'} ${count === 1 ? 'zal' : 'zullen'} worden gewijzigd:`,
};

const MAX_WIDTH = '600px';

type Props = {
  selectedItems: Array<SelectedForm>;
  actionType: ActionType;
  onClose: (hasUpdates: boolean) => void;
};

const ActionModal: React.FCC<Props> = ({
  selectedItems,
  actionType,
  onClose,
}) => {
  const account = useCurrentAccount();
  const trackEvent = useTrackEvent();
  const addToast = useAddToast();
  const [isLoading, setIsLoading] = useState(false);

  const seletedFormIds = selectedItems.map(({ formId }) => formId);

  const [enableForm] = useEnableFormMutation();
  const [deleteForm] = useDeleteFormMutation();

  const onDeleteSelectedFlows = async () => {
    try {
      await executeBatchMutation({
        selectedItems: seletedFormIds,
        actionType: 'delete',
        mutationFn: async id =>
          await deleteForm({
            variables: { formBuilderId: id, accountId: account.id },
          }),
        onClose: () => onClose(true),
        setIsLoading,
        addToast,
        getText: getFormText,
      });
      /** ----- TRACKING: forms_deleted ----- */
      trackEvent({
        eventName: 'forms_deleted',
        parameters: {
          form_ids: seletedFormIds,
        },
      });
    } catch {
      // executeBatchMutation handles errors internally
    }
  };

  const onActivateSelectedFlows = async () => {
    await executeBatchMutation({
      selectedItems: seletedFormIds,
      actionType: 'activate',
      mutationFn: async id => {
        await enableForm({
          variables: {
            formBuilderId: id,
            accountId: account.id,
            enable: true,
          },
        });

        /** ----- TRACKING: forms_enabled----- */
        trackEvent({
          eventName: 'forms_enabled',
          parameters: {
            enabled_count: selectedItems.length,
          },
        });
      },
      onClose: () => onClose(true),
      setIsLoading,
      addToast,
      getText: getFormText,
    });
  };

  const onDeactivateSelectedFlows = async () => {
    await executeBatchMutation({
      selectedItems: seletedFormIds,
      actionType: 'deactivate',
      mutationFn: async id => {
        await enableForm({
          variables: {
            formBuilderId: id,
            accountId: account.id,
            enable: false,
          },
        });

        /** ----- TRACKING: forms_disabled ----- */
        trackEvent({
          eventName: 'forms_disabled',
          parameters: {
            enabled_count: selectedItems.length,
          },
        });
      },

      onClose: () => onClose(true),
      setIsLoading,
      addToast,
      getText: getFormText,
    });
  };

  const buttonPropsByActionType: Record<
    ActionType,
    Pick<ButtonProps, 'label' | 'onClick' | 'appearance'>
  > = {
    delete: {
      appearance: 'danger',
      label: isLoading ? text.loading.delete : text.actions.delete,
      onClick: onDeleteSelectedFlows,
    },
    activate: {
      label: isLoading ? text.loading.activate : text.actions.activate,
      onClick: onActivateSelectedFlows,
      appearance: 'secondary',
    },
    deactivate: {
      label: isLoading ? text.loading.deactivate : text.actions.deactivate,
      onClick: onDeactivateSelectedFlows,
      appearance: 'danger',
    },
  };

  const onCloseModal = () => (isLoading ? undefined : onClose(false));

  return (
    <Overlay onClose={onCloseModal}>
      <Modal onClose={onCloseModal} maxWidth={MAX_WIDTH}>
        <JustificationContainer
          direction="column"
          width="100%"
          padding={['xl', 'xl', 'l', 'xl']}
        >
          <JustificationContainer width="100%" direction="column">
            <JustificationContainer width="100%">
              <Heading3>{`${text.actions[actionType]} ${text.selectedFlows}`}</Heading3>
            </JustificationContainer>
            <JustificationContainer width="100%" direction="column">
              <Body>{getBody(actionType, selectedItems.length)}</Body>
              <JustificationContainer
                width="100%"
                maxHeight="300px"
                height="100%"
                direction="column"
                backgroundColor={{ group: 'tertiary', variant: 'translucent' }}
                border={{ radius: 'base' }}
                padding={['base']}
                style={{ overflowY: 'auto' }}
              >
                <Heading4 margin={[null, null, 'base', null]}>
                  {text.affectedForms(selectedItems.length)}
                </Heading4>
                <JustificationContainer
                  direction="column"
                  gap="xs"
                  padding={[null]}
                >
                  {selectedItems.map(({ formId, formName }) => (
                    <Body key={formId} margin={[null]} size="base">
                      - {formName}
                    </Body>
                  ))}
                </JustificationContainer>
              </JustificationContainer>
            </JustificationContainer>
          </JustificationContainer>
          <JustificationContainer
            margin={['xl', null, null, null]}
            align="center"
            justification="space-between"
            width="100%"
          >
            <TextButton
              padding={[null]}
              label={text.cancelLabel}
              onClick={onCloseModal}
              size="medium"
              appearance="danger"
              disabled={isLoading}
            />
            <Button
              {...buttonPropsByActionType[actionType]}
              size="medium"
              disabled={isLoading}
              loading={isLoading}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const getBody = (actionType: ActionType, selectedCount: number) => {
  switch (actionType) {
    case 'delete':
      return `Weet je zeker dat je ${selectedCount} ${getFormText(
        selectedCount,
      )} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`;
    case 'activate':
      return `Weet je zeker dat je ${selectedCount} ${getFormText(
        selectedCount,
      )} wilt activeren?`;
    case 'deactivate':
      return `Weet je zeker dat je ${selectedCount} ${getFormText(
        selectedCount,
      )} wilt deactiveren?`;

    default:
      return null;
  }
};

export default ActionModal;
